import React, { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
    Autocomplete,
    Chip,
    CircularProgress,
    IconButton,
} from "@mui/material";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";

import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { ButtonVariant } from "Constant/button";
import { routes } from "Constant/routes";
import "pages/Trackers/trackerEvents.css";
import { Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import { createTrackerEventsAsync } from "helpers/trackers/createTrackerEvents";
import { updateTrackerEvent } from "helpers/trackers/updateTrackerEvent";

import StandartButton from "components/atoms/StandartButton";
import MouseOverPopover from "components/MouseOverPopover";
import SwalAlertComp from "components/SwalAlert";

import PageContainer from "../../../../components/atoms/PageContainer";
import { TrackerEventMappingFormProps } from "./TrackerEventMappingForm.types";

const TrackerEventMappingForm = ({
    language,
    trackerEvents,
    eventsCatalog,
    filtersCatalog,
    systemFields,
    isLoadingTrackerEvents,
    trackers,
    dispatchGetTrackerEvents,
    dispatchGetEventsCatalog,
    dispatchGetSystemFields,
    dispatchGetTrackers,
}: TrackerEventMappingFormProps) => {
    const navigate = useNavigate();
    const { id: routeEditId } = useParams();

    useEffect(() => {
        dispatchGetTrackerEvents();
        dispatchGetEventsCatalog();
        dispatchGetSystemFields();
        dispatchGetTrackers();
    }, [
        dispatchGetTrackerEvents,
        dispatchGetEventsCatalog,
        dispatchGetSystemFields,
        dispatchGetTrackers,
    ]);

    const isEditMode = Boolean(routeEditId);
    const isEditDetailLoading = isEditMode && isLoadingTrackerEvents;

    const [showSource, setShowSource] = useState<any>(false);
    const [sourceStandartEvent, setSourceStandartEvent] = useState<any>(false);
    const [destinationStandartEvent, setDestinationStandartEvent] =
        useState<any>(false);
    const [sourceCostumEvent, setSourceCostumEvent] = useState<any>(false);
    const [destinationCostumEvent, setdestinationCostumEvent] =
        useState<any>(false);
    const [showDestination, setShowDestination] = useState<any>(false);
    const [inputSourceText, setInputSourceText] = useState<any>("");
    const [customSourceEvents, setCustomSourceEvents] = useState<any[]>([]);
    const [inputDestinationText, setInputDestinationText] = useState<any>("");
    const [selectedSourceVal, setSelectedSourceVal] = useState<any>(0);
    const [selectedDestinationVal, setSelectedDestinationVal] =
        useState<any>(0);
    const [selectedEvent, setSelectedEvent] = useState<any[]>([]);
    const [destinationEventNewName, setDestinationEventNewName] =
        useState<any>(null);
    const [trackerSourceName, setTrackerSourceName] = useState<any>("");
    const [trackerDestinationName, setTrackerDestinationName] =
        useState<any>("");
    const [destinationStandartEventArray, setDestinationStandartEventArray] =
        useState<any>("");
    const [destinationIsCheck, setDestinationIsCheck] = useState<any>(true);
    const [mappingPrefix, setMappingPrefix] = useState<any>("");
    const [mappingSuffix, setMappingSuffix] = useState<any>("");
    const [mappingType, setMappingType] = useState<any>("Clone");
    const [sourceStandartEventValue, setSourceStandartEventValue] =
        useState<any>("");
    const [destinationStandartEventValue, setDestinationStandartEventValue] =
        useState<any>("");
    const [mappingLoaderStatus, setMappingLoaderStatus] = useState<any>(false);

    const [filterConditions, setFilterConditions] = useState<any[]>([]);
    const [logicalOperator, setLogicalOperator] = useState<any>(null);
    const [customFieldFilters, setCustomFieldFilters] = useState<
        Record<string, any>
    >({});
    const [filterFieldDisplayNames, setFilterFieldDisplayNames] = useState<
        Record<string, any>
    >({});

    // Validation errors state
    const [validationErrors, setValidationErrors] = useState<
        Record<string, any>
    >({});

    // Reset form fields when in create mode
    useEffect(() => {
        if (isEditMode) {
            return;
        }
        setSourceStandartEventValue("");
        setSourceStandartEvent("");
        setSelectedEvent([]);
        setInputSourceText("");
        setCustomSourceEvents([]);
        setSourceCostumEvent("");
        setDestinationStandartEventValue("");
        setDestinationStandartEvent("");
        setDestinationStandartEventArray("");
        setInputDestinationText("");
        setdestinationCostumEvent("");
        setFilterConditions([]);
        setLogicalOperator(null);
        setCustomFieldFilters({});
        setFilterFieldDisplayNames({});
    }, [isEditMode]);

    // ==================== HELPER FUNCTIONS FOR EVENT EDITING ====================

    // Check if event is "All Event"
    const isAllEvent = (eventData) => {
        return (
            eventData.code === "All Event" ||
            eventData.id === -1 ||
            eventData.code === "all_event"
        );
    };

    // Set source event as "All Event"
    const setSourceAsAllEvent = () => {
        setSourceStandartEventValue("all_event");
        setSourceStandartEvent(false);
        setSourceCostumEvent(false);
        setSelectedEvent([]);
        setInputSourceText("");
    };

    // Set source event as standard event
    const setSourceAsStandardEvent = (standardEvent, eventCode) => {
        setSourceStandartEventValue("standart");
        setSourceStandartEvent(true);
        setSourceCostumEvent(false);
        setSelectedEvent([
            {
                name: standardEvent.name,
                code: eventCode,
            },
        ]);
        setInputSourceText("");
    };

    // Set source event as custom event
    const setSourceAsCustomEvent = (eventCode) => {
        setSourceStandartEventValue("custom");
        setSourceStandartEvent(false);
        setSourceCostumEvent(true);
        setCustomSourceEvents(
            Array.isArray(eventCode) ? eventCode : [eventCode],
        );
        setInputSourceText("");
        setSelectedEvent([]);
    };

    // Set destination event as "All Event"
    const setDestinationAsAllEvent = () => {
        setDestinationStandartEventValue("all_event");
        setDestinationStandartEvent(false);
        setdestinationCostumEvent(false);
        setDestinationStandartEventArray("");
        setInputDestinationText("");
    };

    // Set destination event as standard event
    const setDestinationAsStandardEvent = (standardEventCode) => {
        setDestinationStandartEventValue("standart");
        setDestinationStandartEvent(true);
        setdestinationCostumEvent(false);
        setDestinationStandartEventArray(standardEventCode);
        setInputDestinationText("");
        setDestinationEventNewName(eventsCatalog);
    };

    // Set destination event as custom event
    const setDestinationAsCustomEvent = (eventCode) => {
        setDestinationStandartEventValue("custom");
        setDestinationStandartEvent(false);
        setdestinationCostumEvent(true);
        setInputDestinationText(eventCode);
        setDestinationStandartEventArray("");
    };

    // Process source event (All Event / Standard / Custom)
    const processSourceEvent = (sourceEvent) => {
        if (isAllEvent(sourceEvent)) {
            setSourceAsAllEvent();
            return;
        }

        const standardEvent = eventsCatalog.find(
            (item) => item.code === sourceEvent.code,
        );

        if (standardEvent) {
            setSourceAsStandardEvent(standardEvent, sourceEvent.code);
        } else {
            setSourceAsCustomEvent(sourceEvent.code);
        }
    };

    // Process destination event (All Event / Standard / Custom)
    const processDestinationEvent = (destinationEvent) => {
        if (isAllEvent(destinationEvent)) {
            setDestinationAsAllEvent();
            return;
        }

        const standardEvent = eventsCatalog.find(
            (item) => item.code === destinationEvent.code,
        );

        if (standardEvent) {
            setDestinationAsStandardEvent(standardEvent.code);
        } else {
            setDestinationAsCustomEvent(destinationEvent.code);
        }
    };

    // Process filters and reconstruct custom field data
    const processFilters = (filters) => {
        if (!filters?.conditions) {
            setFilterConditions([]);
            setLogicalOperator(null);
            setCustomFieldFilters({});
            setFilterFieldDisplayNames({});
            return;
        }

        setFilterConditions(filters.conditions);
        // Normalize to uppercase to match Select MenuItem values ("AND" / "OR").
        // API/legacy data may store the operator in different casings.
        const normalizedOperator = filters.logical_operator
            ? String(filters.logical_operator).toUpperCase()
            : null;
        setLogicalOperator(normalizedOperator);

        const customFields = {};
        const displayNames = {};

        filters.conditions.forEach((condition, index) => {
            const isSystemField = Object.keys(systemFields).includes(
                condition.field,
            );
            customFields[index] = !isSystemField;
            displayNames[index] = isSystemField
                ? systemFields[condition.field]
                : condition.field;
        });

        setCustomFieldFilters(customFields);
        setFilterFieldDisplayNames(displayNames);
    };

    // ==================== MAIN EDIT EVENT HANDLER ====================

    const handleEditEvent = async (
        event,
        sourceTracker,
        destinationTracker,
    ) => {
        // Set source tracker
        setTrackerSourceName(sourceTracker.id);
        setSelectedSourceVal(sourceTracker.id);
        setShowSource(true);

        // Set destination tracker
        setTrackerDestinationName(destinationTracker.id);
        setSelectedDestinationVal(destinationTracker.id);
        setShowDestination(true);
        setDestinationIsCheck(false);

        // Process events using helper functions
        processSourceEvent(event.source_event);
        processDestinationEvent(event.destination_event);

        // Set mapping properties
        const mappingTypeCapitalized = event.type
            ? event.type.charAt(0).toUpperCase() + event.type.slice(1)
            : "Clone";
        setMappingType(mappingTypeCapitalized);
        setMappingPrefix(event.prefix || "");
        setMappingSuffix(event.suffix || "");

        // Process filters using helper function
        processFilters(event.filters);
    };

    useEffect(() => {
        if (!routeEditId || !trackerEvents?.length) {
            return;
        }
        const eventToEdit = trackerEvents
            .flatMap((group) =>
                group.events.map((evt) => ({
                    event: evt,
                    source_tracker: group.source_tracker,
                    destination_tracker: group.destination_tracker,
                })),
            )
            .find((item) => String(item.event.id) === String(routeEditId));

        if (eventToEdit) {
            handleEditEvent(
                eventToEdit.event,
                eventToEdit.source_tracker,
                eventToEdit.destination_tracker,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeEditId, trackerEvents]);

    const openSource = (val) => {
        setSelectedSourceVal(val);
        setShowSource(val !== "blank");
    };

    const openDestination = (val) => {
        setSelectedDestinationVal(val);
        setShowDestination(val !== "blank");
    };

    const openSourceStandartEvent = (val) => {
        // Handle standard event
        if (val === "standart") {
            setSourceStandartEvent(true);
            setInputSourceText("");
            setCustomSourceEvents([]);
        } else {
            setSourceStandartEvent(false);
        }

        // Handle custom event
        if (val === "custom") {
            setSourceCostumEvent(true);
            setSelectedEvent([]);
        } else {
            setSourceCostumEvent(false);
            setCustomSourceEvents([]);
        }

        // Enable destination selection when event type is selected
        // Don't reset destination fields - preserve user's destination selections
        setDestinationIsCheck(false);
    };

    const openDestinationStandartEvent = (val) => {
        // Handle standard event
        setDestinationStandartEvent(val === "standart");
        if (val === "standart") {
            setInputDestinationText("");
            setDestinationStandartEventArray("");
        }

        // Handle custom event
        setdestinationCostumEvent(val === "custom");
        if (val === "custom") {
            setDestinationStandartEventArray("");
        }

        if (val === "all_event") {
            setInputDestinationText("");
            setDestinationStandartEventArray("");
        }
    };
    const handleSourceTracker = (selectedEventArr) => {
        const selectedItems =
            eventsCatalog &&
            eventsCatalog.length &&
            eventsCatalog.filter((item) =>
                selectedEventArr.some(
                    (selectedEvent) => item?.name === selectedEvent,
                ),
            );
        setSelectedEvent(
            selectedItems?.map((item) => ({
                name: item?.name ?? "",
                code: item?.code ?? "",
            })),
        );

        // Enable destination selection when source events are selected
        // Don't reset destination fields - preserve user's destination selections
        setDestinationIsCheck(false);

        // Clear source event validation error when events are selected
        if (selectedEventArr?.length > 0) {
            clearValidationError("sourceEvent");
        }
    };

    const handleTextSource = (e) => {
        setInputSourceText(e.target.value);
        setDestinationIsCheck(false);
        clearValidationError("sourceEvent");
    };
    const handleTextDestination = (e) => {
        setInputDestinationText(e.target.value);
        clearValidationError("destinationEvent");
    };
    const handleCreateEvents = () => {
        // Validate form first
        const errors = validateForm();
        setValidationErrors(errors);

        // If there are validation errors, show alert and don't proceed
        if (Object.keys(errors).length > 0) {
            SwalAlertComp({
                title: "Validation Error",
                message: "Please fix the highlighted errors before saving.",
                icon: "warning",
                showClass: {
                    popup: "animate_animated animate_fadeInDown",
                },
                hideClass: {
                    popup: "animate_animated animate_fadeOutUp",
                },
                timer: 3000,
            });
            return;
        }

        const destinationEventArrayPopulated = eventsCatalog?.find(
            (item) => item?.code === destinationStandartEventArray,
        )?.code;
        setMappingLoaderStatus(true);
        const events = {
            source_tracker_id:
                trackers &&
                trackers.length > 0 &&
                trackers.find((item) => item?.id === Number(selectedSourceVal))
                    ?.id,
            source_event:
                (customSourceEvents?.length > 0
                    ? customSourceEvents
                    : inputSourceText?.trim()
                      ? [inputSourceText.trim()]
                      : null) ||
                (!!selectedEvent?.length &&
                    selectedEvent?.map((item) => item?.code)) ||
                (sourceStandartEventValue === "all_event"
                    ? ["all_event"]
                    : null),
            destination_tracker_id:
                trackers &&
                trackers.length > 0 &&
                trackers.find(
                    (item) => item?.id === Number(selectedDestinationVal),
                )?.id,
            destination_event:
                inputDestinationText ||
                destinationEventArrayPopulated ||
                (destinationStandartEventValue === "all_event"
                    ? "all_event"
                    : null),
            type: mappingType.toLowerCase(),
            prefix: mappingPrefix !== "" ? mappingPrefix : null,
            suffix: mappingSuffix !== "" ? mappingSuffix : null,
            filters:
                filterConditions.length > 0
                    ? {
                          conditions: filterConditions?.map(
                              ({ field, value, condition }) =>
                                  condition === "isNotEmpty"
                                      ? {
                                            field,
                                            condition,
                                        }
                                      : {
                                            field,
                                            value,
                                            condition,
                                        },
                          ),
                          ...(filterConditions.length > 1 && {
                              logical_operator: logicalOperator,
                          }),
                      }
                    : null,
        };

        if (routeEditId) {
            updateEvent(Number(routeEditId), events);
        } else {
            createEvents(events);
        }
    };
    const createEvents = async (events) => {
        let response = await createTrackerEventsAsync(events);
        if (response?.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                icon: "success",
                showClass: {
                    popup: "animate_animated animate_fadeInDown",
                },
                hideClass: {
                    popup: "animate_animated animate_fadeOutUp",
                },
                timer: 3000,
            });
            resetForm();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message,
                icon: "error",
                showClass: {
                    popup: "animate_animated animate_fadeInDown",
                },
                hideClass: {
                    popup: "animate_animated animate_fadeOutUp",
                },
            });
            setMappingLoaderStatus(false);
        }
    };

    const updateEvent = async (eventId, events) => {
        let response = await updateTrackerEvent(eventId, events);
        if (response?.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                icon: "success",
                showClass: {
                    popup: "animate_animated animate_fadeInDown",
                },
                hideClass: {
                    popup: "animate_animated animate_fadeOutUp",
                },
                timer: 3000,
            });
            resetForm();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response?.message,
                icon: "error",
                showClass: {
                    popup: "animate_animated animate_fadeInDown",
                },
                hideClass: {
                    popup: "animate_animated animate_fadeOutUp",
                },
            });
            setMappingLoaderStatus(false);
        }
    };

    const resetForm = () => {
        setInputSourceText("");
        setCustomSourceEvents([]);
        setInputDestinationText("");
        setSelectedSourceVal(0);
        setSelectedDestinationVal(0);
        setSelectedEvent([]);
        setTrackerSourceName("");
        setTrackerDestinationName("");
        setDestinationStandartEventArray("");
        setDestinationEventNewName(null);
        setDestinationIsCheck(true);
        setShowSource(false);
        setShowDestination(false);
        setSourceStandartEvent(false);
        setSourceStandartEventValue("");
        setDestinationStandartEventValue("");
        setdestinationCostumEvent(false);
        setSourceCostumEvent(false);
        dispatchGetTrackerEvents();
        setMappingPrefix("");
        setMappingType("Clone");
        setMappingSuffix("");
        setMappingLoaderStatus(false);
        setFilterConditions([]);
        setLogicalOperator(null);
        setCustomFieldFilters({});
        setFilterFieldDisplayNames({});
        setValidationErrors({});
        navigate(routes.tracker_events);
    };

    function MyFormControlLabel(props) {
        return <FormControlLabel {...props} />;
    }

    const handleMappingType = (e) => {
        setMappingType(e?.target.value);
    };

    // Validation function that returns detailed errors
    const validateForm = () => {
        const errors: Record<string, any> = {};

        // Source validation - step by step
        // 1. Check if source tracker is selected
        if (!trackerSourceName) {
            errors.sourceTracker =
                language?.validation?.tracker_required ??
                "Please select a tracker.";
        } else if (!showSource) {
            // Tracker selected but showSource is false means tracker selection didn't trigger
            errors.sourceTracker =
                language?.validation?.tracker_required ??
                "Please select a tracker.";
        } else {
            // 2. Check if source event type is selected
            if (!sourceStandartEventValue) {
                errors.sourceEventType =
                    language?.validation?.event_type_required ??
                    "Please select an event type.";
            } else if (sourceStandartEventValue === "standart") {
                // 3. Check if standard event is selected
                if (!selectedEvent?.length) {
                    errors.sourceEvent =
                        language?.validation?.source_event_required ??
                        "Please select at least one event.";
                }
            } else if (sourceStandartEventValue === "custom") {
                // 3. Check if at least one custom event name is provided
                if (!customSourceEvents?.length && !inputSourceText?.trim()) {
                    errors.sourceEvent =
                        language?.validation?.source_event_required ??
                        "Please enter at least one custom event name.";
                }
            }
            // If "all_event" is selected, no additional validation needed
        }

        // Destination validation - step by step
        // 1. Check if destination tracker is selected
        if (!trackerDestinationName) {
            errors.destinationTracker =
                language?.validation?.tracker_required ??
                "Please select a tracker.";
        } else if (!showDestination) {
            errors.destinationTracker =
                language?.validation?.tracker_required ??
                "Please select a tracker.";
        } else {
            // 2. Check if destination event type is selected
            if (!destinationStandartEventValue) {
                errors.destinationEventType =
                    language?.validation?.event_type_required ??
                    "Please select an event type.";
            } else if (destinationStandartEventValue === "standart") {
                // 3. Check if standard event is selected
                if (!destinationStandartEventArray) {
                    errors.destinationEvent =
                        language?.validation?.destination_event_required ??
                        "Please select an event.";
                }
            } else if (destinationStandartEventValue === "custom") {
                // 3. Check if custom event name is provided
                if (!inputDestinationText) {
                    errors.destinationEvent =
                        language?.validation?.destination_event_required ??
                        "Please enter a custom event name.";
                }
            }
            // If "all_event" is selected, no additional validation needed
        }

        // Validate filter conditions
        filterConditions.forEach((condition, index) => {
            const conditionErrors: Record<string, any> = {};

            if (!condition.field) {
                conditionErrors.field =
                    language?.validation?.field_required ??
                    "This field is required.";
            }

            if (!condition.condition) {
                conditionErrors.condition =
                    language?.validation?.condition_required ??
                    "This field is required.";
            }

            if (condition.condition !== "isNotEmpty" && !condition.value) {
                conditionErrors.value =
                    language?.validation?.value_required ??
                    "This field is required.";
            }

            if (Object.keys(conditionErrors).length > 0) {
                errors[`filter_${index}`] = conditionErrors;
            }
        });

        // Validate logical operator (required only if multiple filters)
        if (
            filterConditions.length > 1 &&
            (logicalOperator === null || logicalOperator === "")
        ) {
            errors.logicalOperator =
                language?.validation?.logical_operator_required ??
                "This field is required.";
        }

        return errors;
    };

    // Clear specific validation error when field is updated
    const clearValidationError = (fieldName) => {
        setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[fieldName];
            return newErrors;
        });
    };

    // Clear filter condition specific error
    const clearFilterValidationError = (index, fieldName) => {
        setValidationErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors[`filter_${index}`]) {
                const filterErrors = { ...newErrors[`filter_${index}`] };
                delete filterErrors[fieldName];
                if (Object.keys(filterErrors).length === 0) {
                    delete newErrors[`filter_${index}`];
                } else {
                    newErrors[`filter_${index}`] = filterErrors;
                }
            }
            return newErrors;
        });
    };

    const popoverPrefix = (
        <div className="card">
            <div className="card-body">
                {language?.popover?.prefix_info_desc ??
                    "Prefix is a specific character or text string added to the beginning of an event. This is used to differentiate or categorize event names."}
            </div>
        </div>
    );

    const popoverSuffix = (
        <div className="card">
            <div className="card-body">
                {language?.popover?.suffix_info_desc ??
                    "Suffix is a specific character or text string appended to the end of an event. This is used to add additional information or context to event names."}
            </div>
        </div>
    );

    const popoverClone = (
        <div className="card">
            <div className="card-body">
                {language?.popover?.clone_info_desc ??
                    "Clone creates an exact copy of the existing event. This process typically preserves the current event's configurations and allows progress with a new duplicate."}
            </div>
        </div>
    );

    const popoverReplace = (
        <div className="card">
            <div className="card-body">
                {language?.popover?.replace_info_desc ??
                    "Replace swaps an existing event with the mapped event. This process should be used carefully as it replaces the old item with a new one."}
            </div>
        </div>
    );

    const popoverStandartEvent = (
        <div className="card">
            <div className="card-body">
                {language?.popover?.replace_info_desc ??
                    `A Standard Event is a predefined event used to track specific user actions. These events are typically designed for common and widely used interactions. For example, events like "Add to Cart" or "Purchase" on an e-commerce site are considered Standard Events.`}
            </div>
        </div>
    );

    const popoverCustomEvent = (
        <div className="card">
            <div className="card-body">
                {language?.popover?.replace_info_desc ??
                    `A Custom Event is an event tailored to specific application or user needs. It is used to track unique interactions not covered by standard events. For example, "Level Completed" in a game app or "Workout Started" in a fitness app would be Custom Events.`}
            </div>
        </div>
    );

    useEffect(() => {
        filterConditions?.length <= 1 && setLogicalOperator(null);
    }, [filterConditions?.length]);

    const addFilterCondition = () => {
        const newIndex = filterConditions.length;
        setFilterConditions([
            ...filterConditions,
            {
                field: "",
                value: "",
                condition: "",
            },
        ]);
        setCustomFieldFilters((prev) => ({
            ...prev,
            [newIndex]: false,
        }));
        setFilterFieldDisplayNames((prev) => ({
            ...prev,
            [newIndex]: "",
        }));
    };

    const removeFilterCondition = (index) => {
        const newConditions = filterConditions.filter((_, i) => i !== index);
        setFilterConditions(newConditions);

        const newCustomFieldFilters = {};
        const newDisplayNames = {};
        newConditions.forEach((_, i) => {
            if (customFieldFilters[i] !== undefined) {
                newCustomFieldFilters[i] = customFieldFilters[i];
            }
            if (filterFieldDisplayNames[i] !== undefined) {
                newDisplayNames[i] = filterFieldDisplayNames[i];
            }
        });
        setCustomFieldFilters(newCustomFieldFilters);
        setFilterFieldDisplayNames(newDisplayNames);

        // Clear validation errors for removed filter and re-index remaining ones
        setValidationErrors((prev) => {
            const newErrors = { ...prev };
            // Remove the deleted filter's errors
            delete newErrors[`filter_${index}`];
            // Re-index remaining filter errors
            Object.keys(newErrors).forEach((key) => {
                if (key.startsWith("filter_")) {
                    const filterIndex = parseInt(key.split("_")[1]);
                    if (filterIndex > index) {
                        newErrors[`filter_${filterIndex - 1}`] = newErrors[key];
                        delete newErrors[key];
                    }
                }
            });
            // Clear logical operator error if only 0 or 1 filter remains
            if (newConditions.length <= 1) {
                delete newErrors.logicalOperator;
            }
            return newErrors;
        });
    };

    const updateFilterCondition = (index, field, value) => {
        const newConditions = [...filterConditions];
        newConditions[index] = { ...newConditions[index], [field]: value };
        setFilterConditions(newConditions);
    };

    const handleFieldSelection = (index, value) => {
        const isCustomField = value === "Custom Field";

        // Update custom field flag
        setCustomFieldFilters((prev) => ({
            ...prev,
            [index]: isCustomField,
        }));

        // Set field value and display name
        if (isCustomField) {
            updateFilterCondition(index, "field", "");
            setFilterFieldDisplayNames((prev) => ({
                ...prev,
                [index]: "",
            }));
        } else {
            const fieldLabel = systemFields[value] || value;
            updateFilterCondition(index, "field", value);
            setFilterFieldDisplayNames((prev) => ({
                ...prev,
                [index]: fieldLabel,
            }));
        }
    };

    return (
        <PageContainer
            title={isEditMode ? "Edit Event Mapping" : "Event Mapping"}
            subtitle={
                isEditMode
                    ? "On this page, you can edit the event mapping."
                    : "On this page, you can create an event mapping for the tracker events."
            }
            showBackButton={true}
        >
            <div style={{ overflow: "hidden" }}>
                <Form>
                    <div className="row d-flex justify-between overflow-hidden event-mapping-connector-row">
                        <div className="col-lg-5 col-md-7 event-mapping-form-item max-w-500">
                            <p
                                className="pb-3 trackerEventTitle"
                                style={{ opacity: ".5" }}
                            >
                                {language?.tracker_event?.source ?? "Source"}
                            </p>
                            <div className="event-mapping-form-area source-item">
                                <div
                                    className="card-body p-2"
                                    style={{ height: "100%" }}
                                >
                                    <FormControl
                                        fullWidth
                                        error={!!validationErrors.sourceTracker}
                                    >
                                        <Select
                                            value={trackerSourceName}
                                            label={
                                                language?.tracker_event
                                                    ?.select_tracker ??
                                                "Select Tracker"
                                            }
                                            onChange={(e) => {
                                                setTrackerSourceName(
                                                    e.target.value,
                                                );
                                                openSource(e.target.value);
                                                setSourceStandartEventValue("");
                                                setSourceStandartEvent("");
                                                setSelectedEvent([]);
                                                setInputSourceText("");
                                                setCustomSourceEvents([]);
                                                setSourceCostumEvent("");
                                                clearValidationError(
                                                    "sourceTracker",
                                                );
                                            }}
                                            error={
                                                !!validationErrors.sourceTracker
                                            }
                                        >
                                            {trackers &&
                                                trackers?.length > 0 &&
                                                trackers?.map((item) => {
                                                    return (
                                                        <MenuItem
                                                            key={item?.id}
                                                            value={item?.id}
                                                        >
                                                            {item?.tracker_name}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </Select>
                                        {validationErrors.sourceTracker && (
                                            <FormHelperText>
                                                {validationErrors.sourceTracker}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </div>
                                <div
                                    className="card-body p-2"
                                    style={{ opacity: showSource ? 1 : 0.5 }}
                                >
                                    <Form.Group controlId="formBasicCheckbox">
                                        <FormControl
                                            fullWidth
                                            error={
                                                !!validationErrors.sourceEventType
                                            }
                                        >
                                            <Select
                                                value={sourceStandartEventValue}
                                                label={
                                                    language?.tracker_event
                                                        ?.select_event ??
                                                    "Select Event"
                                                }
                                                disabled={!showSource}
                                                onChange={(e) => {
                                                    openSourceStandartEvent(
                                                        e.target.value,
                                                    );
                                                    setSourceStandartEventValue(
                                                        e.target.value,
                                                    );
                                                    setSelectedEvent([]);
                                                    // Clear validation errors
                                                    clearValidationError(
                                                        "sourceEventType",
                                                    );
                                                    if (
                                                        e.target.value ===
                                                        "all_event"
                                                    ) {
                                                        clearValidationError(
                                                            "sourceEvent",
                                                        );
                                                    }
                                                }}
                                                error={
                                                    !!validationErrors.sourceEventType
                                                }
                                            >
                                                <MenuItem
                                                    key="standart"
                                                    value="standart"
                                                >
                                                    {language?.tracker_event
                                                        ?.standart_event ??
                                                        "Standard Event"}
                                                </MenuItem>
                                                <MenuItem
                                                    key="custom"
                                                    value="custom"
                                                >
                                                    {language?.tracker_event
                                                        ?.custom_event ??
                                                        "Custom Event"}
                                                </MenuItem>
                                                <MenuItem
                                                    key="all_event"
                                                    value="all_event"
                                                >
                                                    {language?.tracker_event
                                                        ?.use_all_event ??
                                                        "All Events"}
                                                </MenuItem>
                                            </Select>
                                            {validationErrors.sourceEventType && (
                                                <FormHelperText>
                                                    {
                                                        validationErrors.sourceEventType
                                                    }
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                        {sourceStandartEvent && (
                                            <FormControl
                                                fullWidth
                                                className="mouse-over-popover-area mt-3"
                                            >
                                                <Autocomplete
                                                    multiple
                                                    freeSolo
                                                    options={
                                                        eventsCatalog?.length
                                                            ? Array.from(
                                                                  new Set(
                                                                      eventsCatalog?.map(
                                                                          (
                                                                              option,
                                                                          ) =>
                                                                              option.name,
                                                                      ),
                                                                  ),
                                                              )
                                                            : []
                                                    }
                                                    value={selectedEvent?.map(
                                                        (event) => event?.name,
                                                    )}
                                                    renderInput={(params) => {
                                                        return (
                                                            <TextField
                                                                {...params}
                                                                label="Standard Event"
                                                                slotProps={{
                                                                    input: {
                                                                        ...params.InputProps,
                                                                        type: "search",
                                                                    },
                                                                }}
                                                                error={
                                                                    !!validationErrors.sourceEvent
                                                                }
                                                                helperText={
                                                                    validationErrors.sourceEvent
                                                                }
                                                            />
                                                        );
                                                    }}
                                                    renderOption={(
                                                        props,
                                                        option,
                                                        { inputValue },
                                                    ) => {
                                                        const {
                                                            key,
                                                            ...optionProps
                                                        } = props;
                                                        const matches = match(
                                                            option,
                                                            inputValue,
                                                            {
                                                                insideWords: true,
                                                            },
                                                        );
                                                        const parts = parse(
                                                            option,
                                                            matches,
                                                        );
                                                        return (
                                                            <li
                                                                key={key}
                                                                {...optionProps}
                                                            >
                                                                <div>
                                                                    {parts?.map(
                                                                        (
                                                                            part,
                                                                            index,
                                                                        ) => (
                                                                            <span
                                                                                key={
                                                                                    index
                                                                                }
                                                                                style={{
                                                                                    background:
                                                                                        part.highlight
                                                                                            ? "gray"
                                                                                            : "unset",
                                                                                }}
                                                                            >
                                                                                {
                                                                                    part.text
                                                                                }
                                                                            </span>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            </li>
                                                        );
                                                    }}
                                                    onChange={(_, newValue) => {
                                                        handleSourceTracker(
                                                            newValue,
                                                        );
                                                    }}
                                                    disableCloseOnSelect
                                                />
                                                <MouseOverPopover
                                                    withAutocomplete
                                                    text={popoverStandartEvent}
                                                ></MouseOverPopover>
                                            </FormControl>
                                        )}
                                        {sourceCostumEvent && (
                                            <FormControl
                                                className="mouse-over-popover-area custom-event-popover-area"
                                                style={{
                                                    width: "100%",
                                                }}
                                            >
                                                <Autocomplete
                                                    multiple
                                                    freeSolo
                                                    options={[]}
                                                    value={customSourceEvents}
                                                    inputValue={inputSourceText}
                                                    onInputChange={(
                                                        _,
                                                        newInputValue,
                                                    ) => {
                                                        setInputSourceText(
                                                            newInputValue,
                                                        );
                                                    }}
                                                    onChange={(_, newValue) => {
                                                        // Filter out empty strings and trim whitespace
                                                        const cleaned = newValue
                                                            .map((v) =>
                                                                v.trim(),
                                                            )
                                                            .filter(
                                                                (v) => v !== "",
                                                            );
                                                        setCustomSourceEvents(
                                                            cleaned,
                                                        );
                                                        setDestinationIsCheck(
                                                            false,
                                                        );
                                                        if (
                                                            cleaned.length > 0
                                                        ) {
                                                            clearValidationError(
                                                                "sourceEvent",
                                                            );
                                                        }
                                                    }}
                                                    renderTags={(
                                                        value,
                                                        getTagProps,
                                                    ) =>
                                                        value.map(
                                                            (option, index) => (
                                                                <Chip
                                                                    onClick={() => {}}
                                                                    label={
                                                                        option
                                                                    }
                                                                    size="small"
                                                                    {...getTagProps(
                                                                        {
                                                                            index,
                                                                        },
                                                                    )}
                                                                    key={index}
                                                                />
                                                            ),
                                                        )
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            className="mt-3"
                                                            label={
                                                                language
                                                                    ?.tracker_event
                                                                    ?.custome_event_name ??
                                                                "Custom Event Name"
                                                            }
                                                            placeholder={
                                                                customSourceEvents.length ===
                                                                0
                                                                    ? (language
                                                                          ?.tracker_event
                                                                          ?.custom_event_placeholder ??
                                                                      "Type and press Enter to add")
                                                                    : ""
                                                            }
                                                            style={{
                                                                width: "100%",
                                                            }}
                                                            error={
                                                                !!validationErrors.sourceEvent
                                                            }
                                                            helperText={
                                                                validationErrors.sourceEvent
                                                            }
                                                        />
                                                    )}
                                                />
                                                <MouseOverPopover
                                                    text={popoverCustomEvent}
                                                ></MouseOverPopover>
                                            </FormControl>
                                        )}
                                    </Form.Group>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-5 col-md-7 event-mapping-form-item max-w-500">
                            <p
                                className="pb-3 trackerEventTitle"
                                style={{ opacity: ".5" }}
                            >
                                {language?.tracker_event?.destination ??
                                    "Destination"}
                            </p>
                            <div
                                className={`event-mapping-form-area destination-item ${
                                    destinationIsCheck ? "disabled" : ""
                                }`}
                            >
                                <div className="card-body p-2">
                                    <FormControl
                                        fullWidth
                                        error={
                                            !!validationErrors.destinationTracker
                                        }
                                    >
                                        <Select
                                            disabled={destinationIsCheck}
                                            value={trackerDestinationName}
                                            label={
                                                language?.tracker_event
                                                    ?.select_tracker ??
                                                "Select Tracker"
                                            }
                                            onChange={(e) => {
                                                setTrackerDestinationName(
                                                    e.target.value,
                                                );
                                                openDestination(e.target.value);
                                                setDestinationStandartEventValue(
                                                    "",
                                                );
                                                setDestinationStandartEvent("");
                                                setDestinationStandartEventArray(
                                                    "",
                                                );
                                                setInputDestinationText("");
                                                setdestinationCostumEvent("");
                                                clearValidationError(
                                                    "destinationTracker",
                                                );
                                            }}
                                            error={
                                                !!validationErrors.destinationTracker
                                            }
                                        >
                                            {trackers &&
                                                trackers?.length > 0 &&
                                                trackers?.map((item) => {
                                                    return (
                                                        <MenuItem
                                                            key={item?.id}
                                                            value={item?.id}
                                                        >
                                                            {item?.tracker_name}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </Select>
                                        {validationErrors.destinationTracker && (
                                            <FormHelperText>
                                                {
                                                    validationErrors.destinationTracker
                                                }
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </div>
                                <div
                                    className="card-body p-2"
                                    style={{
                                        opacity: showDestination ? 1 : 0.5,
                                    }}
                                >
                                    <Form.Group controlId="formBasicCheckbox">
                                        <FormControl
                                            fullWidth
                                            error={
                                                !!validationErrors.destinationEventType
                                            }
                                        >
                                            <Select
                                                disabled={!showDestination}
                                                value={
                                                    destinationStandartEventValue
                                                }
                                                label={
                                                    language?.tracker_event
                                                        ?.select_event ??
                                                    "Select Event"
                                                }
                                                onChange={(e) => {
                                                    openDestinationStandartEvent(
                                                        e.target.value,
                                                    );
                                                    setDestinationStandartEventValue(
                                                        e.target.value,
                                                    );
                                                    // Use existing eventsCatalog instead of making another API call
                                                    setDestinationEventNewName(
                                                        eventsCatalog,
                                                    );
                                                    // Clear validation errors
                                                    clearValidationError(
                                                        "destinationEventType",
                                                    );
                                                    if (
                                                        e.target.value ===
                                                        "all_event"
                                                    ) {
                                                        clearValidationError(
                                                            "destinationEvent",
                                                        );
                                                    }
                                                }}
                                                error={
                                                    !!validationErrors.destinationEventType
                                                }
                                            >
                                                <MenuItem
                                                    key="standart"
                                                    value="standart"
                                                >
                                                    {language?.tracker_event
                                                        ?.standart_event ??
                                                        "Standard Event"}
                                                </MenuItem>
                                                <MenuItem
                                                    key="custom"
                                                    value="custom"
                                                >
                                                    {language?.tracker_event
                                                        ?.custom_event ??
                                                        "Custom Event"}
                                                </MenuItem>
                                                <MenuItem
                                                    key="all_event"
                                                    value="all_event"
                                                >
                                                    {language?.tracker_event
                                                        ?.use_all_event ??
                                                        "All Events"}
                                                </MenuItem>
                                            </Select>
                                            {validationErrors.destinationEventType && (
                                                <FormHelperText>
                                                    {
                                                        validationErrors.destinationEventType
                                                    }
                                                </FormHelperText>
                                            )}
                                        </FormControl>

                                        {destinationStandartEvent &&
                                        destinationEventNewName?.length ===
                                            0 ? (
                                            <FormControl
                                                fullWidth
                                                className="mt-3 mouse-over-popover-area"
                                                error={
                                                    !!validationErrors.destinationEvent
                                                }
                                            >
                                                <Select
                                                    value={
                                                        destinationStandartEventArray
                                                    }
                                                    label={
                                                        language?.tracker_event
                                                            ?.standart_events ??
                                                        "Standard Event"
                                                    }
                                                    onChange={(e) => {
                                                        setDestinationStandartEventArray(
                                                            e.target.value,
                                                        );
                                                        clearValidationError(
                                                            "destinationEvent",
                                                        );
                                                    }}
                                                    error={
                                                        !!validationErrors.destinationEvent
                                                    }
                                                >
                                                    {eventsCatalog &&
                                                        eventsCatalog.length >
                                                            0 &&
                                                        eventsCatalog.map(
                                                            (item) => {
                                                                return (
                                                                    <MenuItem
                                                                        key={
                                                                            item?.code
                                                                        }
                                                                        value={
                                                                            item?.code
                                                                        }
                                                                    >
                                                                        {
                                                                            item?.name
                                                                        }
                                                                    </MenuItem>
                                                                );
                                                            },
                                                        )}
                                                </Select>
                                                {validationErrors.destinationEvent && (
                                                    <FormHelperText>
                                                        {
                                                            validationErrors.destinationEvent
                                                        }
                                                    </FormHelperText>
                                                )}
                                                <MouseOverPopover
                                                    text={popoverStandartEvent}
                                                    withAutocomplete
                                                ></MouseOverPopover>
                                            </FormControl>
                                        ) : destinationStandartEvent &&
                                          destinationEventNewName?.length ? (
                                            <FormControl
                                                fullWidth
                                                className="mouse-over-popover-area mt-3"
                                            >
                                                <Autocomplete
                                                    freeSolo
                                                    options={
                                                        destinationEventNewName?.length
                                                            ? Array.from(
                                                                  new Set(
                                                                      destinationEventNewName?.map(
                                                                          (
                                                                              option,
                                                                          ) =>
                                                                              option.name,
                                                                      ),
                                                                  ),
                                                              )
                                                            : []
                                                    }
                                                    value={
                                                        destinationEventNewName?.find(
                                                            (item) =>
                                                                item.code ===
                                                                destinationStandartEventArray,
                                                        )?.name ||
                                                        destinationStandartEventArray ||
                                                        ""
                                                    }
                                                    renderInput={(params) => {
                                                        return (
                                                            <TextField
                                                                {...params}
                                                                label="Standard Event"
                                                                slotProps={{
                                                                    input: {
                                                                        ...params.InputProps,
                                                                        type: "search",
                                                                    },
                                                                }}
                                                                error={
                                                                    !!validationErrors.destinationEvent
                                                                }
                                                                helperText={
                                                                    validationErrors.destinationEvent
                                                                }
                                                            />
                                                        );
                                                    }}
                                                    renderOption={(
                                                        props,
                                                        option,
                                                        { inputValue },
                                                    ) => {
                                                        const {
                                                            key,
                                                            ...optionProps
                                                        } = props;
                                                        const matches = match(
                                                            option,
                                                            inputValue,
                                                            {
                                                                insideWords: true,
                                                            },
                                                        );
                                                        const parts = parse(
                                                            option,
                                                            matches,
                                                        );
                                                        return (
                                                            <li
                                                                key={key}
                                                                {...optionProps}
                                                            >
                                                                <div>
                                                                    {parts?.map(
                                                                        (
                                                                            part,
                                                                            index,
                                                                        ) => (
                                                                            <span
                                                                                key={
                                                                                    index
                                                                                }
                                                                                style={{
                                                                                    background:
                                                                                        part.highlight
                                                                                            ? "gray"
                                                                                            : "unset",
                                                                                }}
                                                                            >
                                                                                {
                                                                                    part.text
                                                                                }
                                                                            </span>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            </li>
                                                        );
                                                    }}
                                                    onChange={(_, newValue) => {
                                                        // Convert name to code for consistency
                                                        const selectedEvent =
                                                            destinationEventNewName?.find(
                                                                (item) =>
                                                                    item.name ===
                                                                    newValue,
                                                            );
                                                        setDestinationStandartEventArray(
                                                            selectedEvent
                                                                ? selectedEvent.code
                                                                : newValue,
                                                        );
                                                        if (newValue) {
                                                            clearValidationError(
                                                                "destinationEvent",
                                                            );
                                                        }
                                                    }}
                                                />

                                                <MouseOverPopover
                                                    text={popoverStandartEvent}
                                                    withAutocomplete
                                                ></MouseOverPopover>
                                            </FormControl>
                                        ) : null}
                                        {destinationCostumEvent && (
                                            <FormControl
                                                className="mouse-over-popover-area custom-event-popover-area"
                                                style={{
                                                    width: "100%",
                                                }}
                                            >
                                                <TextField
                                                    className="mt-3"
                                                    label={
                                                        language?.tracker_event
                                                            ?.custome_event_name ??
                                                        "Custom Event Name"
                                                    }
                                                    value={inputDestinationText}
                                                    onChange={
                                                        handleTextDestination
                                                    }
                                                    style={{
                                                        width: "100%",
                                                    }}
                                                    error={
                                                        !!validationErrors.destinationEvent
                                                    }
                                                    helperText={
                                                        validationErrors.destinationEvent
                                                    }
                                                />
                                                <MouseOverPopover
                                                    text={popoverCustomEvent}
                                                ></MouseOverPopover>
                                            </FormControl>
                                        )}
                                    </Form.Group>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-4">
                        <div className="event-mapping-form-area">
                            <div className="row">
                                <div className="col-md-6 col-sm-12 mb-3">
                                    <FormControl
                                        style={{ width: "100%" }}
                                        className="mouse-over-popover-area"
                                    >
                                        <TextField
                                            label={
                                                language?.tracker_event
                                                    ?.prefix ??
                                                "Prefix (optional)"
                                            }
                                            value={mappingPrefix}
                                            onChange={(e) => {
                                                setMappingPrefix(
                                                    e?.target?.value,
                                                );
                                            }}
                                            style={{ width: "100%" }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                        />
                                        <MouseOverPopover
                                            text={popoverPrefix}
                                        ></MouseOverPopover>
                                    </FormControl>
                                </div>
                                <div className="col-md-6 col-sm-12 mb-3">
                                    <FormControl
                                        style={{ width: "100%" }}
                                        className="mouse-over-popover-area"
                                    >
                                        <TextField
                                            label={
                                                language?.tracker_event
                                                    ?.suffix ??
                                                "Suffix (optional)"
                                            }
                                            value={mappingSuffix}
                                            onChange={(e) => {
                                                setMappingSuffix(
                                                    e?.target?.value,
                                                );
                                            }}
                                            style={{ width: "100%" }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                        />
                                        <MouseOverPopover
                                            text={popoverSuffix}
                                        ></MouseOverPopover>
                                    </FormControl>
                                </div>
                                <div className="col-sm-12">
                                    <RadioGroup
                                        onChange={handleMappingType}
                                        name="use-radio-group"
                                        value={mappingType}
                                        className="d-flex flex-row gap-2 popover-group"
                                    >
                                        <FormControl className="mouse-over-popover-area">
                                            <MyFormControlLabel
                                                value={"Clone"}
                                                label={
                                                    language?.tracker_event
                                                        ?.clone ?? "Clone"
                                                }
                                                control={<Radio />}
                                            />
                                            <MouseOverPopover
                                                text={popoverClone}
                                            ></MouseOverPopover>
                                        </FormControl>
                                        <FormControl className="ml-5 mouse-over-popover-area">
                                            <MyFormControlLabel
                                                value={"Replace"}
                                                label={
                                                    language?.tracker_event
                                                        ?.replace ?? "Replace"
                                                }
                                                control={<Radio />}
                                            />
                                            <MouseOverPopover
                                                text={popoverReplace}
                                            ></MouseOverPopover>
                                        </FormControl>
                                    </RadioGroup>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4">
                        <div className="event-mapping-form-area">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center mouse-over-popover-area">
                                    <h6 className="mb-0">
                                        {language?.tracker_event?.filters ??
                                            "Filters (Optional)"}
                                        {filterConditions.length > 0 && (
                                            <Chip
                                                label={`${
                                                    filterConditions.length
                                                } condition${
                                                    filterConditions.length > 1
                                                        ? "s"
                                                        : ""
                                                }`}
                                                size="small"
                                                style={{
                                                    marginLeft: "8px",
                                                }}
                                                onClick={() => {}}
                                            />
                                        )}
                                    </h6>
                                </div>
                            </div>

                            {
                                <div className="filters-container">
                                    {filterConditions.length > 0 && (
                                        <div className="mb-3">
                                            <FormControl
                                                fullWidth
                                                error={
                                                    !!validationErrors.logicalOperator
                                                }
                                            >
                                                <Select
                                                    value={
                                                        logicalOperator ?? ""
                                                    }
                                                    onChange={(e) => {
                                                        setLogicalOperator(
                                                            e.target.value,
                                                        );
                                                        clearValidationError(
                                                            "logicalOperator",
                                                        );
                                                    }}
                                                    disabled={
                                                        !(
                                                            filterConditions?.length >
                                                            1
                                                        )
                                                    }
                                                    label="Logical Operator"
                                                    error={
                                                        !!validationErrors.logicalOperator
                                                    }
                                                >
                                                    <MenuItem value="AND">
                                                        AND
                                                    </MenuItem>
                                                    <MenuItem value="OR">
                                                        OR
                                                    </MenuItem>
                                                </Select>
                                                {validationErrors.logicalOperator && (
                                                    <FormHelperText>
                                                        {
                                                            validationErrors.logicalOperator
                                                        }
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        </div>
                                    )}

                                    {filterConditions.map(
                                        (condition, index) => (
                                            <div
                                                key={index}
                                                className="filter-condition-row mb-3 p-3 border rounded"
                                            >
                                                <div className="row">
                                                    <div className="col-md-4 col-sm-12 mb-2">
                                                        {customFieldFilters[
                                                            index
                                                        ] ? (
                                                            <TextField
                                                                fullWidth
                                                                label="Custom Field"
                                                                value={
                                                                    condition.field
                                                                }
                                                                onChange={(
                                                                    e,
                                                                ) => {
                                                                    updateFilterCondition(
                                                                        index,
                                                                        "field",
                                                                        e.target
                                                                            .value,
                                                                    );
                                                                    clearFilterValidationError(
                                                                        index,
                                                                        "field",
                                                                    );
                                                                }}
                                                                placeholder="e.g., campaign id"
                                                                error={
                                                                    !!validationErrors[
                                                                        `filter_${index}`
                                                                    ]?.field
                                                                }
                                                                helperText={
                                                                    validationErrors[
                                                                        `filter_${index}`
                                                                    ]?.field
                                                                }
                                                            />
                                                        ) : (
                                                            <Autocomplete
                                                                options={[
                                                                    ...Object.entries(
                                                                        systemFields,
                                                                    ).map(
                                                                        ([
                                                                            key,
                                                                            value,
                                                                        ]) => ({
                                                                            label: value,
                                                                            value: key,
                                                                        }),
                                                                    ),
                                                                    {
                                                                        label: "Custom Field",
                                                                        value: "Custom Field",
                                                                    },
                                                                ]}
                                                                value={
                                                                    filterFieldDisplayNames[
                                                                        index
                                                                    ]
                                                                        ? {
                                                                              label: filterFieldDisplayNames[
                                                                                  index
                                                                              ],
                                                                              value: filterFieldDisplayNames[
                                                                                  index
                                                                              ],
                                                                          }
                                                                        : null
                                                                }
                                                                onChange={(
                                                                    _,
                                                                    newValue,
                                                                ) => {
                                                                    handleFieldSelection(
                                                                        index,
                                                                        newValue?.value ||
                                                                            newValue,
                                                                    );
                                                                    clearFilterValidationError(
                                                                        index,
                                                                        "field",
                                                                    );
                                                                }}
                                                                renderInput={(
                                                                    params,
                                                                ) => (
                                                                    <TextField
                                                                        {...params}
                                                                        fullWidth
                                                                        label="Field"
                                                                        placeholder="Select field"
                                                                        error={
                                                                            !!validationErrors[
                                                                                `filter_${index}`
                                                                            ]
                                                                                ?.field
                                                                        }
                                                                        helperText={
                                                                            validationErrors[
                                                                                `filter_${index}`
                                                                            ]
                                                                                ?.field
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="col-md-3 col-sm-12 mb-2">
                                                        <FormControl
                                                            fullWidth
                                                            error={
                                                                !!validationErrors[
                                                                    `filter_${index}`
                                                                ]?.condition
                                                            }
                                                        >
                                                            <Select
                                                                value={
                                                                    condition.condition
                                                                }
                                                                onChange={(
                                                                    e,
                                                                ) => {
                                                                    updateFilterCondition(
                                                                        index,
                                                                        "condition",
                                                                        e.target
                                                                            .value,
                                                                    );
                                                                    clearFilterValidationError(
                                                                        index,
                                                                        "condition",
                                                                    );
                                                                }}
                                                                label="Condition"
                                                                error={
                                                                    !!validationErrors[
                                                                        `filter_${index}`
                                                                    ]?.condition
                                                                }
                                                            >
                                                                {filtersCatalog.map(
                                                                    (
                                                                        filter,
                                                                    ) => (
                                                                        <MenuItem
                                                                            key={
                                                                                filter.code
                                                                            }
                                                                            value={
                                                                                filter.code
                                                                            }
                                                                        >
                                                                            {
                                                                                filter.name
                                                                            }
                                                                        </MenuItem>
                                                                    ),
                                                                )}
                                                            </Select>
                                                            {validationErrors[
                                                                `filter_${index}`
                                                            ]?.condition && (
                                                                <FormHelperText>
                                                                    {
                                                                        validationErrors[
                                                                            `filter_${index}`
                                                                        ]
                                                                            ?.condition
                                                                    }
                                                                </FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    </div>
                                                    {condition.condition !==
                                                        "isNotEmpty" && (
                                                        <div className="col-md-4 col-sm-12 mb-2">
                                                            <TextField
                                                                fullWidth
                                                                label="Value"
                                                                value={
                                                                    condition.value
                                                                }
                                                                onChange={(
                                                                    e,
                                                                ) => {
                                                                    updateFilterCondition(
                                                                        index,
                                                                        "value",
                                                                        e.target
                                                                            .value,
                                                                    );
                                                                    clearFilterValidationError(
                                                                        index,
                                                                        "value",
                                                                    );
                                                                }}
                                                                placeholder="1930234512"
                                                                error={
                                                                    !!validationErrors[
                                                                        `filter_${index}`
                                                                    ]?.value
                                                                }
                                                                helperText={
                                                                    validationErrors[
                                                                        `filter_${index}`
                                                                    ]?.value
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                    <div
                                                        className="col-md-1 col-sm-12 d-flex align-items-center justify-content-center"
                                                        style={{
                                                            marginLeft:
                                                                condition.condition ===
                                                                "isNotEmpty"
                                                                    ? "auto"
                                                                    : "0px",
                                                        }}
                                                    >
                                                        <IconButton
                                                            color="error"
                                                            onClick={() =>
                                                                removeFilterCondition(
                                                                    index,
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </div>
                                                </div>
                                            </div>
                                        ),
                                    )}
                                    <StandartButton
                                        variant={ButtonVariant.BLACK_OUTLINED}
                                        onClick={addFilterCondition}
                                    >
                                        <AddIcon />
                                        {language?.buttons?.add_filter ??
                                            "Add Filter Condition"}
                                    </StandartButton>
                                </div>
                            }
                        </div>
                    </div>
                    <div
                        className="mt-4"
                        style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                        <Button
                            color="primary"
                            disabled={mappingLoaderStatus}
                            onClick={() => handleCreateEvents()}
                            variant="contained"
                        >
                            {language?.buttons?.save ?? "Save"}
                        </Button>
                    </div>
                </Form>
            </div>
        </PageContainer>
    );
};

export default TrackerEventMappingForm;
